import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Url from '../../../data/url';
import {
  useMokStdRequestMutation,
  useLoginMutation,
  useKakaoLoginMutation,
  useMyInfoQuery,
} from '../../../queries';
import { useUserStore } from '../../../store/useUserStore';
import toast from 'react-hot-toast';
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  SIGNUP_TYPE,
  ON_KIDS_MAIN_KID_ID,
} from '../../../data/define';
import IcLogin from '../../../assets/image/ic_pass_login.svg';
import FullHeightContainer from '../../../component/common/FullHeightContainer';
import OneBtnAlert from '../../../component/common/OneBtnAlert';
import { useTranslation } from '../../../hook/useTranslation';

// 같은 계정으로 다시 로그인했을 때 이전에 선택한 아이를 유지한다(user-172)
function savedMainKid<T extends { id: number }>(kids: T[] = []): T | undefined {
  try {
    const savedId = localStorage.getItem(ON_KIDS_MAIN_KID_ID);
    return kids.find((kid) => String(kid.id) === savedId);
  } catch {
    return undefined;
  }
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { search } = useLocation();
  const signupType = new URLSearchParams(search).get('type');
  const historyParam = new URLSearchParams(search).get('history'); // 부모 프로필 수정에서 진입했을 때의 파라미터
  const setUser = useUserStore((s) => s.setUser);
  const [hasAuthorized, setHasAuthorized] = useState(false);
  const [loginGuideAlertOpen, setLoginGuideAlertOpen] = useState(false);

  const isFromParentProfileEdit = historyParam === 'profile';

  const { mutateAsync: login } = useLoginMutation();
  const { mutateAsync: kakaoLogin } = useKakaoLoginMutation();
  const { mutateAsync: mokStdRequest } = useMokStdRequestMutation();

  const { data: myInfo } = useMyInfoQuery({
    enabled: hasAuthorized,
  });

  useEffect(() => {
    if (myInfo) {
      setUser((prev) => {
        return {
          ...prev,
          ...myInfo,
          isLoggedIn: true,
          isSignup: myInfo.isSignup,
          mainKid: savedMainKid(myInfo.kids) || myInfo.kids[0] || null,
        };
      });
      if (myInfo.isSignup) {
        navigate(Url.LoginWelcome, { replace: true });
      } else {
        if (isFromParentProfileEdit) {
          navigate(Url.MyParentProfile, { replace: true, state: { history: 'login' } });
        } else {
          navigate(Url.Home, { replace: true });
        }
      }
    }
  }, [myInfo, isFromParentProfileEdit, setUser, navigate]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      console.log('React Native로부터 원본 메시지 수신:', event.data);

      try {
        const messageData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        if (messageData.type === 'authCompleted' && messageData.success) {
          const authData = messageData.data;
          console.log('인증 성공! 로그인 프로세스를 시작합니다.', authData);
          if (authData) {
            handleAuthComplete(authData);
          }
        } else if (messageData.type === 'LOGIN_WITH_KAKAO') {
          console.log('카카오 로그인 성공! 로그인 프로세스를 시작합니다.', messageData);
          handleKakaoLoginComplete(messageData.data);
        }
      } catch (e) {
        console.error('React Native 메시지를 파싱하거나 처리하는데 실패했습니다.', e);
      }
    };

    if (window.ReactNativeWebView) {
      console.log('React Native WebView 환경입니다. message 리스너를 등록합니다.');
      window.addEventListener('message', handleMessage);
      document.addEventListener('message', handleMessage as any);
    }

    return () => {
      if (window.ReactNativeWebView) {
        window.removeEventListener('message', handleMessage);
        document.removeEventListener('message', handleMessage as any);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuthComplete = async (authData: any) => {
    if (!authData) {
      console.error('authData가 유효하지 않습니다.');
      return;
    }

    console.log('앱에서 받은 정보로 로그인을 시도합니다:', authData);
    try {
      const response = await login({
        name: authData.userName,
        birth: authData.userBirthday,
        phoneNumber: authData.userPhone,
        agreeEventBenefit: true,
      });
      const data = response.data;
      setHasAuthorized(true);
      localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    } catch (e: any) {
      console.error('로그인 API 호출 중 에러 발생', e);
      if (
        e.response?.status === 400 &&
        e.response?.data?.message === '이미 카카오로 가입된 사용자입니다.'
      ) {
        setLoginGuideAlertOpen(true);
      }
    }
  };

  const handleKakaoLoginComplete = async (kakaoData: any) => {
    const normalizePhoneNumber = (phoneNumber: string) => {
      if (!phoneNumber) return '';
      let normalized = String(phoneNumber);
      if (normalized.startsWith('+82')) {
        normalized = normalized.replace(/^\+82\s*/, '');
      }
      normalized = normalized.replace(/\D/g, '');
      if (normalized.startsWith('10') && normalized.length >= 10) {
        normalized = '0' + normalized;
      }
      return normalized;
    };

    if (!kakaoData) {
      console.error('kakaoData가 유효하지 않습니다.');
      return;
    }

    console.log('앱에서 받은 정보로 로그인을 시도합니다:', kakaoData);
    try {
      const response = await kakaoLogin({
        providerUserId: String(kakaoData.id),
        name: kakaoData.name,
        birth: kakaoData.birthyear + kakaoData.birthday,
        phoneNumber: normalizePhoneNumber(kakaoData.phoneNumber || ''),
        agreeEventBenefit: true,
      });
      const data = response.data;
      setHasAuthorized(true);
      localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
      navigate(Url.Root, { replace: true });
    } catch (e: any) {
      console.error('로그인 API 호출 중 에러 발생', e);
      if (
        e.response?.status === 400 &&
        e.response?.data?.message === '이미 PASS로 가입된 사용자입니다.'
      ) {
        setLoginGuideAlertOpen(true);
      }
    }
  };

  const handleAuthClick = async () => {
    try {
      // PASS 표준인증창 URL 생성
      const configRes = await mokStdRequest();
      const config = configRes.data;
      const params = new URLSearchParams(config).toString();
      const popupUrl = `https://cert.mobile-ok.com/ptb_mokauth.html?${params}`;

      // 데스크탑 팝업오픈
      if (!window.ReactNativeWebView) {
        const popup = window.open('', 'mokAuthPopup', 'width=400,height=600');
        if (!popup) {
          toast.error(t('login.pass.toast_popup_blocked'));
          return;
        }
        popup.location.href = popupUrl;

        // JSON 데이터 폴링
        const intervalId = window.setInterval(async () => {
          try {
            if (!popup || popup.closed) {
              clearInterval(intervalId);
              return;
            }
            const bodyText = popup.document.body.innerText.trim();
            if (bodyText.startsWith('{') && bodyText.endsWith('}')) {
              clearInterval(intervalId);
              try {
                const info = JSON.parse(bodyText).data;
                const response = await login({
                  name: info.userName,
                  birth: info.userBirthday,
                  phoneNumber: info.userPhone,
                  agreeEventBenefit: true,
                });
                const data = response.data;
                setHasAuthorized(true);
                localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
                localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
                popup.close();
              } catch (e: any) {
                console.error('로그인 호출 중 에러', e);
                if (
                  e.response?.status === 400 &&
                  e.response?.data?.message === '이미 카카오로 가입된 사용자입니다.'
                ) {
                  popup.close();
                  setLoginGuideAlertOpen(true);
                  return;
                }
                return;
              }
            }
          } catch {
            // cross-origin ignore during polling
          }
        }, 200);
      } else {
        // 모바일 팝업오픈
        window.open(popupUrl, 'mokAuthPopup', 'width=400,height=600');
      }
    } catch (err) {
      console.error('인증 요청 실패', err);
    }
  };

  const handleKakaoLogin = async () => {
    try {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: 'LOGIN_WITH_KAKAO',
          })
        );
      } else {
        toast.error(t('login.pass.toast_kakao_mobile'));
      }
    } catch (err) {
      console.error('인증 요청 실패', err);
    }
  };


  // 로컬용 작업용 회원가입 로직(문자PASS대체)
  const handleTempSignUp = async () => {
    try {
      // 개발자 정보
      const apiParams = {
        providerUserId: '',
        name: '김카라', // PASS완료 계정의 이름
        birth: '1990-01-01', // 생년월일
        phoneNumber: '01012345678', // 전화번호
        agreeEventBenefit: true,
      };
      if (!apiParams.name || !apiParams.birth || !apiParams.phoneNumber) {
        toast.error(t('login.pass.temp_signup_toast'));
        return;
      }
      if (signupType === SIGNUP_TYPE.PASS) {
        const response = await login(apiParams);
        const data = response.data;
        localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
        setHasAuthorized(true);
      } else {
        const response = await kakaoLogin(apiParams);
        const data = response.data;
        localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
        setHasAuthorized(true);
      }
    } catch (error: any) {
      console.error('임시 회원가입 실패:', error);
      console.log(error);
      if (error.response?.status === 400) {
        setLoginGuideAlertOpen(true);
      }
    }
  };

  const simpleType = signupType === SIGNUP_TYPE.PASS ? t('login.kakao_simple') : t('login.pass_simple');

  return (
    <FullHeightContainer showBackButton>
      <div className="pageInner bg-[#FFF] w-full flex flex-col items-center justify-center">
        <div className="w-full flex flex-col items-center justify-start text-[28px] font-[800] text-center">
          {isFromParentProfileEdit ? (
            <>
              {t('login.pass.title_edit').split('\n').map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </>
          ) : (
            <>
              {t('login.pass.title_signup').split('\n').map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </>
          )}
        </div>
        <div className="mt-[10px] mb-[40px] text-center">{t('login.pass.subtitle')}</div>
        <div className="w-full flex items-center justify-center">
          <img src={IcLogin} alt="PASS로 본인인증" className="w-140px h-140px" />
        </div>
        {window.location.hostname !== 'onkiz.co.kr' && (
          <button type="button" onClick={handleTempSignUp} className="mt-4 text-gray-400 text-sm">
            {t('login.pass.temp_signup')}
          </button>
        )}
      </div>
      <div className="w-full">
        <div className="w-full flex justify-center mb-[20px] items-center gap-2 text-[#767676] font-[400] text-[16px] px-[20px]">
          <button
            type="button"
            className="w-full font-[700] text-[#fff] p-3 rounded-[8px]"
            onClick={handleTempSignUp}
            style={{ background: '#3a99ff', color: '#fff' }}
          >
            {t('login.pass.temp_signup_btn')}
          </button>
        </div>
        <div className="w-full flex justify-center mb-[20px] items-center gap-2 text-[#767676] font-[400] text-[16px] px-[20px]">
          {signupType === SIGNUP_TYPE.PASS ? (
            <button
              type="button"
              className="w-full font-[700] text-[#fff] p-3 rounded-[8px]"
              onClick={handleAuthClick}
              style={{ background: '#FF3A4A', color: '#fff' }}
            >
              {t('login.pass.btn_pass')}
            </button>
          ) : (
            <button
              type="button"
              className="w-full font-[700] text-[#371D1E] p-3 rounded-[8px]"
              onClick={handleKakaoLogin}
              style={{ background: '#FAE100' }}
            >
              {t('login.pass.btn_kakao')}
            </button>
          )}
        </div>
        <div className="text-[#969696] text-[14px] mb-[20px] text-center">
          {t('login.pass.footer').split('\n').map((line, i) => (
            <span key={i}>
              {line}
              {i === 0 && <br />}
            </span>
          ))}
        </div>
      </div>

      <OneBtnAlert
        open={loginGuideAlertOpen}
        onClose={() => setLoginGuideAlertOpen(false)}
        title={t('login.pass.alert_already_title')}
        description={t('login.pass.alert_already_desc', { type: simpleType })}
        btnText={t('login.pass.alert_already_btn', { type: simpleType })}
        onClick={() => {
          setLoginGuideAlertOpen(false);
          if (signupType === SIGNUP_TYPE.PASS) {
            navigate(`${Url.LoginPass}?type=kakao`, { replace: true });
          } else {
            navigate(`${Url.LoginPass}?type=pass`, { replace: true });
          }
        }}
      />
    </FullHeightContainer>
  );
}
